import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { PRODUCT_SORT_OPTIONS } from "../helper/products";


const EMPTY_OPTIONS = { series_options: [], set_options: [] };

const TABS = {
  FILTERS: "filters",
  OPTIONS: "options",
};

function asFilterList(value) {
  if (Array.isArray(value)) return value;
  if (value) return [value];
  return [];
}

const SearchFiltersComponent = ({
  filters,
  onChange,
  options = EMPTY_OPTIONS,
  displayOptions,
  onDisplayOptionsChange,
}) => {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(TABS.FILTERS);

  const seriesOptions = options.series_options || [];
  const sortOptions = PRODUCT_SORT_OPTIONS;

  const selectedSeries = asFilterList(filters.series);
  const selectedSets = asFilterList(filters.set);

  const scopedSets = useMemo(() => {
    if (selectedSeries.length > 0) {
      const sets = selectedSeries.flatMap(
        (seriesName) => seriesOptions.find((entry) => entry.series === seriesName)?.sets || []
      );
      return [...new Set(sets)].sort((a, b) => a.localeCompare(b));
    }
    return [...(options.set_options || [])].sort((a, b) => a.localeCompare(b));
  }, [selectedSeries, seriesOptions, options.set_options]);

  const activeFilterCount =
    (filters.sort ? 1 : 0) + selectedSeries.length + selectedSets.length;

  const openSheet = (tab) => {
    setActiveTab(tab);
    setOpen(true);
  };

  const toggleSeries = (seriesName) => {
    const nextSeries = selectedSeries.includes(seriesName)
      ? selectedSeries.filter((name) => name !== seriesName)
      : [...selectedSeries, seriesName];

    const allowedSets = new Set(
      nextSeries.length > 0
        ? nextSeries.flatMap(
            (name) => seriesOptions.find((entry) => entry.series === name)?.sets || []
          )
        : options.set_options || []
    );

    onChange({
      ...filters,
      series: nextSeries,
      set: selectedSets.filter((setName) => allowedSets.has(setName)),
    });
  };

  const toggleSet = (setName) => {
    const nextSets = selectedSets.includes(setName)
      ? selectedSets.filter((name) => name !== setName)
      : [...selectedSets, setName];
    onChange({ ...filters, set: nextSets });
  };

  const clearFilters = () => {
    onChange({ sort: "", series: [], set: [] });
  };

  const setHideProductDetails = (hideProductDetails) => {
    onDisplayOptionsChange({ ...displayOptions, hideProductDetails });
  };

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const filterSheet = (
    <>
      <div
        className={`filters-sheet-backdrop ${open ? "open" : ""}`}
        onClick={() => setOpen(false)}
        aria-hidden={!open}
      />

      <section
        id="product-browse-sheet"
        className={`filters-sheet ${open ? "open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Browse filters and options"
        aria-hidden={!open}
      >
        <div className="filters-sheet-header">
          <div className="browse-sheet-tabs" role="tablist" aria-label="Browse panel">
            <button
              type="button"
              role="tab"
              id="browse-tab-filters"
              className={`browse-sheet-tab ${activeTab === TABS.FILTERS ? "is-active" : ""}`}
              aria-selected={activeTab === TABS.FILTERS}
              aria-controls="browse-panel-filters"
              onClick={() => setActiveTab(TABS.FILTERS)}
            >
              Filters
              {activeFilterCount > 0 && (
                <span className="browse-sheet-tab-badge">{activeFilterCount}</span>
              )}
            </button>
            <button
              type="button"
              role="tab"
              id="browse-tab-options"
              className={`browse-sheet-tab ${activeTab === TABS.OPTIONS ? "is-active" : ""}`}
              aria-selected={activeTab === TABS.OPTIONS}
              aria-controls="browse-panel-options"
              onClick={() => setActiveTab(TABS.OPTIONS)}
            >
              Options
            </button>
          </div>
          <button
            type="button"
            className="filters-sheet-close"
            onClick={() => setOpen(false)}
            aria-label="Close panel"
          >
            ×
          </button>
        </div>

        <div className="filters-sheet-body">
          {activeTab === TABS.FILTERS && (
            <div
              id="browse-panel-filters"
              role="tabpanel"
              aria-labelledby="browse-tab-filters"
              className="search-filters-grid"
            >
              <label className="search-filter-field search-filter-field-sort">
                <span>Sort</span>
                <select
                  value={filters.sort || ""}
                  onChange={(e) => onChange({ ...filters, sort: e.target.value })}
                >
                  <option value="">Default (in stock first)</option>
                  {sortOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>

              <div className="filters-options-columns">
                <div className="search-filter-field">
                  <span>Series {selectedSeries.length > 0 && `(${selectedSeries.length})`}</span>
                  <div
                    className="filter-checkbox-list filter-checkbox-list--series"
                    role="group"
                    aria-label="Series"
                  >
                    {seriesOptions.length === 0 ? (
                      <p className="filter-checkbox-empty">No series available</p>
                    ) : (
                      seriesOptions.map((entry) => (
                        <label key={entry.series} className="filter-checkbox-option">
                          <input
                            type="checkbox"
                            checked={selectedSeries.includes(entry.series)}
                            onChange={() => toggleSeries(entry.series)}
                          />
                          <span>{entry.series}</span>
                        </label>
                      ))
                    )}
                  </div>
                </div>

                <div className="search-filter-field">
                  <span>Set {selectedSets.length > 0 && `(${selectedSets.length})`}</span>
                  <div
                    className="filter-checkbox-list filter-checkbox-list--sets"
                    role="group"
                    aria-label="Set"
                  >
                    {scopedSets.length === 0 ? (
                      <p className="filter-checkbox-empty">No sets available</p>
                    ) : (
                      scopedSets.map((setName) => (
                        <label key={setName} className="filter-checkbox-option">
                          <input
                            type="checkbox"
                            checked={selectedSets.includes(setName)}
                            onChange={() => toggleSet(setName)}
                          />
                          <span>{setName}</span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === TABS.OPTIONS && (
            <div
              id="browse-panel-options"
              role="tabpanel"
              aria-labelledby="browse-tab-options"
              className="browse-options-grid"
            >
              <p className="browse-options-lead">Customize how products appear in the grid.</p>
              <label className="browse-option-toggle">
                <input
                  type="checkbox"
                  checked={!!displayOptions?.hideProductDetails}
                  onChange={(e) => setHideProductDetails(e.target.checked)}
                />
                <span className="browse-option-toggle-text">
                  <strong>Hide product details on cards</strong>
                  <small>Hides price, stock, conditions, and quick actions on each card.</small>
                </span>
              </label>
            </div>
          )}
        </div>

        <div className="filters-sheet-actions">
          {activeTab === TABS.FILTERS ? (
            <>
              <button
                type="button"
                className="filters-sheet-clear"
                onClick={clearFilters}
                disabled={activeFilterCount === 0}
              >
                Clear
              </button>
              <button
                type="button"
                className="filters-sheet-apply"
                onClick={() => setOpen(false)}
              >
                Done
              </button>
            </>
          ) : (
            <button
              type="button"
              className="filters-sheet-apply browse-options-done"
              onClick={() => setOpen(false)}
            >
              Done
            </button>
          )}
        </div>
      </section>
    </>
  );

  return (
    <>
      <div className="browse-triggers">
        <button
          type="button"
          className="filters-trigger"
          onClick={() => openSheet(TABS.FILTERS)}
          aria-expanded={open && activeTab === TABS.FILTERS}
          aria-controls="product-browse-sheet"
        >
          Filters
          {activeFilterCount > 0 && (
            <span className="filters-trigger-badge">{activeFilterCount}</span>
          )}
        </button>
        <button
          type="button"
          className="filters-trigger browse-trigger-options"
          onClick={() => openSheet(TABS.OPTIONS)}
          aria-expanded={open && activeTab === TABS.OPTIONS}
          aria-controls="product-browse-sheet"
        >
          Options
        </button>
      </div>

      {createPortal(filterSheet, document.body)}
    </>
  );
};

export default SearchFiltersComponent;
